import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useSession } from "@/api/auth";
import { apiFetch } from "@/api/client";
import { useLastProjectStore } from "@/stores/last-project";

// Wire-format mirrors the projects routes in
// src/flowstack/server/routes/projects.py (B1 PR landed the FastAPI
// readers). Status union widens to the canonical LifecycleStateLabel
// from src/flowstack/lifecycle/state_machine.py — the GUI never
// computes a state; backend is authoritative.

/** Canonical run lifecycle labels (mirrors
 * src/flowstack/lifecycle/state_machine.LifecycleStateLabel). */
export type LifecycleStateLabel =
  | "validating"
  | "cached"
  | "staging_inputs"
  | "dispatched"
  | "running"
  | "collecting_outputs"
  | "validating_outputs"
  | "completed"
  | "failed_validation"
  | "failed_system"
  | "failed_contract"
  | "failed_scientific";

/** Where a run's `started_at`/`finished_at` came from (mirrors
 * src/flowstack/server/run_timing.TimingSource). */
export type TimingSource = "vcm" | "lifecycle";

export interface Project {
  project_id: string;
  tenant_id: string;
  slug: string;
  display_name: string;
  description: string | null;
  // Real project records (fs#1207): an `archived` project keeps its runs and
  // stays readable but takes no new runs. Optional because it is additive.
  status?: "active" | "archived";
  created_at: string;
  // Aggregate counts the projects-list page shows without a second
  // fetch. Backend computes these once at query time.
  run_count: number;
  active_run_count: number;
  artifact_count: number;
}

export interface ProjectListResponse {
  projects: Project[];
  // How many projects the tenant has, counted apart from `projects`
  // (flowstack#1866). `projects.length === total` is the runtime proof the
  // list is WHOLE; the rail's Projects and Runs badges show only then
  // (`wholeProjects` in components/layout/Sidebar.tsx). Optional because it
  // is additive: an older server omits it, and the rail then shows no badge.
  total?: number;
  // Whether this viewer may create, rename and archive projects — an
  // `org_owner` or platform admin (fs#1207). It only decides which controls
  // render; the server re-checks the role on every write. Absent → false.
  can_manage?: boolean;
}

// Run rollup that the detail page needs alongside the project header.
// Same wire shape the backend's /api/projects/<slug>/runs endpoint
// emits — keys mirror the catalog's run-lifecycle row shape.
//
// ``project_slug`` is optional because the project-scoped endpoint
// (/api/projects/<slug>/runs) omits it (the consumer already knows
// which project the row belongs to); the global /api/runs endpoint
// emits it on every row so the global run list can render a Project
// column.
export interface ProjectRun {
  run_id: string;
  flow_name: string | null; // null for single-Tile dispatches
  tile_name: string | null; // null for Flow runs
  // The flow NODE this run is a step of; absent/null for a standalone Tile run.
  flow_node_name?: string | null;
  // The flow SUBMISSION this run is a step of (flowstack#1799) — the key the
  // Runs tab groups on. Absent/null for a standalone Tile run and for a flow
  // child written before the column existed. Optional because it is additive:
  // an older server omits it.
  flow_run_id?: string | null;
  status: LifecycleStateLabel;
  submitted_at: string;
  // The run's window, both ends from the ONE source `timing_source` names
  // (flowstack#961). `vcm` is the compute window VCM recorded (#1270), used
  // when it has both ends; `lifecycle` is the run's first entry into `running`
  // and first entry into a terminal state, on FlowStack's clock. A `null` end
  // means **not recorded** and NOTHING else: a cache hit never entered
  // `running`, so it has an end and no start, and a run in flight has no end.
  //
  // It is never evidence ABOUT the run — not a cache hit, not a fast run, not
  // a failure. An investigation on this project already read `started_at:
  // null` as "served from cache" across 300 rows of which zero were
  // populated. Render absence through `components/runs/run-timing.ts`, which
  // says "not recorded" rather than leaving a blank that reads as zero.
  started_at: string | null;
  finished_at: string | null;
  // Additive (flowstack#961): absent from an older server. `null` when the run
  // has no end from either source.
  timing_source?: TimingSource | null;
  // The compute provider FlowStack routed the run to (`atlas` / `stub`).
  // Additive (flowstack#961). `null` for a flow run's own row and for a
  // standalone run that never reached dispatch.
  provider?: string | null;
  submitted_by: string;
  project_slug?: string;
}

export interface ProjectRunsResponse {
  runs: ProjectRun[];
}

/** The prefix every project query key shares — list, detail and the
 * project-scoped run feed. Invalidate THIS to drop all of them at once. */
export const PROJECTS_ROOT_QUERY_KEY = ["projects"] as const;

export const PROJECTS_QUERY_KEY = [...PROJECTS_ROOT_QUERY_KEY, "list"] as const;

export function projectQueryKey(slug: string): readonly unknown[] {
  return [...PROJECTS_ROOT_QUERY_KEY, "detail", slug] as const;
}

export function projectRunsQueryKey(slug: string): readonly unknown[] {
  return [...PROJECTS_ROOT_QUERY_KEY, "runs", slug] as const;
}

/** The cache holds the whole `ProjectListResponse` under `PROJECTS_QUERY_KEY`,
 * `total` included, so the rail can check the list is whole; callers of this
 * hook get just the `projects` array through `select`. */
export function useProjects() {
  return useQuery({
    queryKey: PROJECTS_QUERY_KEY,
    queryFn: fetchProjectList,
    select: selectProjects,
    staleTime: 30_000,
  });
}

/** The same cache entry as {@link useProjects}, with `total` kept, for a
 * caller that sums over the list and so must check it is whole first
 * ({@link wholeProjectList}). */
export function useProjectList() {
  return useQuery({
    queryKey: PROJECTS_QUERY_KEY,
    queryFn: fetchProjectList,
    staleTime: 30_000,
  });
}

function fetchProjectList(): Promise<ProjectListResponse> {
  return apiFetch<ProjectListResponse>("/api/projects");
}

function selectProjects(response: ProjectListResponse): Project[] {
  return response.projects;
}

/**
 * The projects, only when the response proves they are ALL of them —
 * `total` present and equal to the row count (flowstack#1866). A sum over
 * anything less is a page-sum, not a tenant total, so callers render no
 * number rather than a short one.
 */
export function wholeProjectList(response: ProjectListResponse): Project[] | undefined {
  if (typeof response.total !== "number") return undefined;
  return response.projects.length === response.total ? response.projects : undefined;
}

/** Whether the viewer may manage projects, read off the list's cache entry
 * (fs#1207). `false` until the list has loaded, so no control flashes. */
export function useCanManageProjects(): boolean {
  return useProjectList().data?.can_manage === true;
}

/** Every project write changes the list, the detail and their counts: drop
 * the whole `projects` prefix. */
function useProjectWrite<TVars>(write: (vars: TVars) => Promise<Project>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_ROOT_QUERY_KEY });
    },
  });
}

/** `POST /api/projects` — 409 `conflict` when the org has the name already. */
export function useCreateProject() {
  return useProjectWrite((name: string) =>
    apiFetch<Project>("/api/projects", { method: "POST", json: { name } })
  );
}

/** `PATCH /api/projects/{id}` — renames; the slug, and so the URL, is kept. */
export function useRenameProject() {
  return useProjectWrite(({ projectId, name }: { projectId: string; name: string }) =>
    apiFetch<Project>(`/api/projects/${encodeURIComponent(projectId)}`, {
      method: "PATCH",
      json: { name },
    })
  );
}

/** `POST /api/projects/{id}/archive` — there is no unarchive in Phase 1. */
export function useArchiveProject() {
  return useProjectWrite((projectId: string) =>
    apiFetch<Project>(`/api/projects/${encodeURIComponent(projectId)}/archive`, {
      method: "POST",
    })
  );
}

export function useProject(slug: string) {
  return useQuery({
    queryKey: projectQueryKey(slug),
    queryFn: async (): Promise<Project> => {
      return apiFetch<Project>(`/api/projects/${encodeURIComponent(slug)}`);
    },
    staleTime: 30_000,
    enabled: Boolean(slug),
  });
}

/**
 * Remember `slug` as this viewer's last-used project (`stores/last-project`),
 * once the server has confirmed the project exists — so a mistyped or stale
 * URL never replaces a good entry. Called from every page under
 * `/projects/:slug`; it reads the same cached project query those pages do.
 */
export function useRememberProject(slug: string): void {
  const { data: session } = useSession();
  const project = useProject(slug);
  const remember = useLastProjectStore((s) => s.remember);
  const confirmed = project.data?.slug === slug ? slug : null;
  const userId = session?.user_id;
  const tenantId = session?.tenant_id;
  React.useEffect(() => {
    if (confirmed === null || userId === undefined || tenantId === undefined) return;
    remember({ user_id: userId, tenant_id: tenantId, slug: confirmed });
  }, [confirmed, userId, tenantId, remember]);
}

export function useProjectRuns(slug: string) {
  return useQuery({
    queryKey: projectRunsQueryKey(slug),
    queryFn: async (): Promise<ProjectRun[]> => {
      const response = await apiFetch<ProjectRunsResponse>(
        `/api/projects/${encodeURIComponent(slug)}/runs`
      );
      return response.runs;
    },
    // Runs change more often than the project header — short stale.
    staleTime: 10_000,
    enabled: Boolean(slug),
  });
}
